import { Component, ChangeDetectionStrategy, input, output, signal, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { User, UserPlan } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-user-details',
  standalone: true,
  template: `
<div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
  <div class="flex items-center mb-6">
    <button (click)="onBack()" class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 mr-3">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-gray-600">
        <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
      </svg>
    </button>
    <h2 class="text-2xl font-bold text-slate-900">
      {{ isEditMode() ? 'Editar usuário' : 'Criar novo usuário' }}
    </h2>
  </div>

  <form #userForm="ngForm" (ngSubmit)="onSave()" class="space-y-6">
    <div>
      <h3 class="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">Informações pessoais</h3>
      <div class="space-y-4">
        <div>
          <label for="name" class="block text-sm font-medium text-gray-700">Nome completo</label>
          <input type="text" id="name" name="name" [(ngModel)]="name" required
                 class="mt-1 block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-slate-800 dark:text-gray-200 placeholder-gray-500 focus:z-10 focus:border-custom-blue focus:outline-none focus:ring-custom-blue sm:text-sm">
        </div>
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700">E-mail</label>
          <input type="email" id="email" name="email" [(ngModel)]="email" required
                 class="mt-1 block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-slate-800 dark:text-gray-200 placeholder-gray-500 focus:z-10 focus:border-custom-blue focus:outline-none focus:ring-custom-blue sm:text-sm">
        </div>
        <div>
          <label for="plan" class="block text-sm font-medium text-gray-700">Plano</label>
          <select id="plan" name="plan" [ngModel]="plan()" (ngModelChange)="onPlanChange($event)"
            class="mt-1 block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-slate-800 dark:text-gray-200 placeholder-gray-500 focus:z-10 focus:border-custom-blue focus:outline-none focus:ring-custom-blue sm:text-sm">
            @for (p of availablePlans; track p) {
              <option [value]="p">{{ p.charAt(0).toUpperCase() + p.slice(1) }}</option>
            }
          </select>
        </div>
      </div>
    </div>

    <div>
      <h3 class="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">Gerenciar créditos</h3>
      <div class="flex items-end space-x-4">
        <div class="flex-grow">
          <label for="credits" class="block text-sm font-medium text-gray-700">Créditos atuais</label>
          <input type="number" id="credits" name="credits" [(ngModel)]="credits" required
                 class="mt-1 block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-slate-800 dark:text-gray-200 placeholder-gray-500 focus:z-10 focus:border-custom-blue focus:outline-none focus:ring-custom-blue sm:text-sm">
        </div>
        <div class="flex-grow">
          <label for="add-credits" class="block text-sm font-medium text-gray-700">Adicionar/remover</label>
          <input type="number" id="add-credits" name="add-credits" [(ngModel)]="creditsToAdd"
                 class="mt-1 block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-slate-800 dark:text-gray-200 placeholder-gray-500 focus:z-10 focus:border-custom-blue focus:outline-none focus:ring-custom-blue sm:text-sm">
        </div>
        <button type="button" (click)="onAddCredits()" class="h-10 px-4 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-colors">
          Aplicar
        </button>
      </div>
    </div>

    <div class="border-t pt-6 space-y-4">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h3 class="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2 sm:mb-0">Outras ações</h3>
        @if (isEditMode()) {
          <button type="button" (click)="onResetPassword()" [disabled]="isResettingPassword()" class="w-full sm:w-auto text-sm bg-yellow-100 text-yellow-800 font-semibold py-2 px-4 rounded-lg hover:bg-yellow-200 transition-colors disabled:opacity-60">
            {{ isResettingPassword() ? 'Enviando...' : 'Resetar senha' }}
          </button>
        }
      </div>

      <div class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-4 space-y-2 space-y-reverse sm:space-y-0">
        <button type="button" (click)="onBack()" class="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50">
          Cancelar
        </button>
        <button type="submit" [disabled]="!userForm.form.valid" class="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-custom-blue hover:bg-indigo-700 disabled:bg-gray-400">
          {{ isEditMode() ? 'Salvar alterações' : 'Criar usuário' }}
        </button>
      </div>

      @if (isEditMode()) {
        <div class="border-t border-red-200 pt-6 mt-6">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-medium text-red-700">Zona de perigo</h3>
              <p class="text-sm text-gray-500">Esta ação é irreversível.</p>
            </div>
            <button type="button" (click)="onDelete()" class="bg-red-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors">
              Excluir usuário
            </button>
          </div>
        </div>
      }
    </div>
  </form>
</div>
`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule]
})
export class UserDetailsComponent {
  user = input<User | null>(null);

  userCreate = output<Pick<User, 'name' | 'email' | 'credits' | 'plan'>>();
  userUpdate = output<User>();
  userDelete = output<string>();
  back = output<void>();

  private authService = inject(AuthService);

  isEditMode = signal(false);
  isResettingPassword = signal(false);
  name = signal('');
  email = signal('');
  credits = signal(0);
  creditsToAdd = signal(0);
  plan = signal<UserPlan>('gratuito');

  readonly availablePlans: UserPlan[] = ['gratuito', 'basico', 'intermediario', 'pro'];
  private readonly PLAN_CREDITS: Record<UserPlan, number> = {
    gratuito: 1,
    basico: 4,
    intermediario: 8,
    pro: 999
  };

  constructor() {
    effect(() => {
      const currentUser = this.user();
      this.isEditMode.set(!!currentUser);
      if (currentUser) {
        this.name.set(currentUser.name);
        this.email.set(currentUser.email);
        this.credits.set(currentUser.credits);
        this.plan.set(currentUser.plan || 'gratuito');
      } else {
        this.name.set('');
        this.email.set('');
        this.credits.set(0);
        this.plan.set('gratuito');
      }
      this.creditsToAdd.set(0);
    });
  }

  onPlanChange(newPlan: UserPlan) {
    this.plan.set(newPlan);
    this.credits.set(this.PLAN_CREDITS[newPlan]);
  }

  onSave() {
    if (this.isEditMode() && this.user()) {
      const updatedUser: User = {
        ...this.user()!,
        name: this.name(),
        email: this.email(),
        credits: this.credits(),
        plan: this.plan()
      };
      this.userUpdate.emit(updatedUser);
    } else {
      this.userCreate.emit({
        name: this.name(),
        email: this.email(),
        credits: this.credits(),
        plan: this.plan()
      });
    }
  }

  onDelete() {
    if (this.user()) {
      this.userDelete.emit(this.user()!.id);
    }
  }

  async onResetPassword() {
    const email = this.email().trim();
    if (!email) {
      this.authService.showNotification('error', 'Reset de senha', 'O usuário precisa ter um e-mail válido.');
      return;
    }

    this.isResettingPassword.set(true);
    try {
      await this.authService.requestPasswordReset(email);
      this.authService.showNotification('success', 'Reset de senha', `O link de redefinição foi enviado para ${email}.`);
    } catch (error: any) {
      console.error('Password reset failed:', error);
      this.authService.showNotification('error', 'Reset de senha', error.message ?? 'Não foi possível enviar o e-mail de redefinição.');
    } finally {
      this.isResettingPassword.set(false);
    }
  }

  onAddCredits() {
    this.credits.update(c => c + this.creditsToAdd());
    this.creditsToAdd.set(0);
  }

  onBack() {
    this.back.emit();
  }
}
